import React, { useState, useMemo, useEffect, useRef, lazy, Suspense } from "react";
import RaceSeriesMenu from "./components/RaceSeriesMenu";
import FantasyMainScreen from "./components/nascar/fantasy/FantasyMainScreen";
import { dataPreloader } from "./utils/dataPreloader";
import { readStored, writeStored } from "./utils/storage";
import { hashTabName } from "./utils/hashParams";
import ErrorBoundary from "./components/nascar/shared/ErrorBoundary";
import analytics, { trackPageView, trackThemeChange, trackTabSwitch, trackTimeSpent, trackError } from "./utils/analytics";
import {
  createTheme,
  ThemeProvider,
  CssBaseline,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
} from "@mui/material";

// Fantasy is the landing tab; the other tabs (and their chart libraries) load on first use
const StatisticsMainScreen = lazy(() => import("./components/nascar/statistics/StatisticsMainScreen"));
const FavoriteDriverMenu = lazy(() => import("./components/nascar/favorite_driver/FavoriteDriverMenu"));
const HistoryMainScreen = lazy(() => import("./components/nascar/history/HistoryMainScreen"));

const TabLoading = () => (
  <Box sx={{ textAlign: "center", mt: 4 }}>
    <CircularProgress />
  </Box>
);

// The top-level tab lives in the URL hash so a view can be bookmarked and shared (#statistics)
const TAB_HASH = { Fantasy: "fantasy", Statistics: "statistics", Favourite_driver: "driver", History: "history" };
const tabFromHash = () => {
  const name = hashTabName(); // the view state after "?" belongs to the tab's own screen
  return Object.keys(TAB_HASH).find((tab) => TAB_HASH[tab] === name) || "Fantasy";
};
const THEME_KEY = "beonapex.theme";

const App = () => {
  const [themeMode, setThemeMode] = useState(() => (readStored(THEME_KEY) === "light" ? "light" : "dark")); // dark unless the viewer chose light
  const [selectedNascarTab, setSelectedNascarTab] = useState(tabFromHash);
  
  // Analytics tracking refs
  const pageStartTime = useRef(null);
  const currentTab = useRef(selectedNascarTab);

  // Initialize analytics
  useEffect(() => {
    analytics.initialize();
    pageStartTime.current = Date.now();
    trackPageView(currentTab.current, "BeOnApex - NASCAR Fantasy Analytics");
  }, []);

  // Global error handling with analytics
  useEffect(() => {
    const handleError = (event) => {
      const error = event.error || event.reason;
      console.error("🔥 Global error caught:", error);
      trackError(error, 'global_error_handler');
    };

    const handleUnhandledRejection = (event) => {
      console.error("🔥 Unhandled promise rejection:", event.reason);
      trackError(event.reason, 'unhandled_rejection');
      event.preventDefault(); // Prevent the default browser error handling
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Warm up shared static data in the background without blocking the first render
  useEffect(() => {
    dataPreloader.preloadStaticData();
  }, []);

  // Create theme dynamically based on state
  const theme = useMemo(
    () =>
      createTheme({
        components: {
          // Grid items otherwise refuse to shrink below their widest table, which stretches the whole page on a phone
          MuiGrid: { styleOverrides: { root: { minWidth: 0 } } },
        },
        palette: {
          mode: themeMode,
          ...(themeMode === "dark"
            ? {
                background: { default: "#1E1E1E", paper: "#252525" },
                text: { primary: "#E0E0E0", secondary: "#B0B0B0" },
                primary: { main: "#90CAF9" },
                secondary: { main: "#F48FB1" },
                divider: "#444444",
              }
            : {
                background: { default: "#FFFFFF", paper: "#F5F5F5" },
                text: { primary: "#000000", secondary: "#444444" },
                primary: { main: "#1976D2" },
                secondary: { main: "#D81B60" },
                divider: "#CCCCCC",
              }),
        },
      }),
    [themeMode]
  );

  const handleChangeTheme = (event) => {
    const newTheme = event.target.value;
    setThemeMode(newTheme);
    writeStored(THEME_KEY, newTheme);
    trackThemeChange(newTheme);
  };

  const handleNascarSubMenuChange = (newTab) => {
    // Track time spent on previous tab
    if (pageStartTime.current && currentTab.current) {
      const timeSpent = Math.round((Date.now() - pageStartTime.current) / 1000);
      trackTimeSpent(currentTab.current.toLowerCase(), timeSpent);
    }

    // Track tab switch
    trackTabSwitch(currentTab.current, newTab);
    
    // Update state and refs
    setSelectedNascarTab(newTab);
    currentTab.current = newTab;
    if (tabFromHash() !== newTab) window.location.hash = TAB_HASH[newTab];
    pageStartTime.current = Date.now();
    
    // Track new page view
    const pageTitleMap = {
      'Fantasy': 'BeOnApex - NASCAR Fantasy Analytics',
      'Statistics': 'BeOnApex - NASCAR Statistics & Results',
      'Favourite_driver': 'BeOnApex - Favorite Driver Tracking',
      'History': 'BeOnApex - NASCAR Historical Statistics'
    };
    
    trackPageView(newTab, pageTitleMap[newTab] || `BeOnApex - ${newTab}`);
  };

  // Browser back/forward (or a pasted link) changes the hash: follow it
  useEffect(() => {
    const onHashChange = () => {
      const tab = tabFromHash();
      if (tab !== currentTab.current) handleNascarSubMenuChange(tab);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  });

  return (
    <ErrorBoundary showDetails={true}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            // beside the tabs on larger screens; on a phone it is a row of its own above them
            position: { xs: "static", sm: "absolute" },
            right: "40px",
            top: "10px",
            zIndex: 1,
            display: "flex",
            justifyContent: "flex-end",
            pr: { xs: 2, sm: 0 },
          }}
        >
          <RadioGroup value={themeMode} row onChange={handleChangeTheme}>
            <FormControlLabel control={<Radio />} value="dark" label="Dark" />
            <FormControlLabel control={<Radio />} value="light" label="Light" />
          </RadioGroup>
        </Box>

        <Box
          sx={{
            bgcolor: "background.default",
            color: "text.primary",
            minHeight: "100vh",
            p: 0,
          }}
        >
          <div
            style={{ backgroundColor: theme.palette.background.paper }}
          >
            <ErrorBoundary showDetails={true}>
              <RaceSeriesMenu selectedTab={selectedNascarTab} onNascarSubMenuChange={handleNascarSubMenuChange} />
            </ErrorBoundary>
          </div>

          {/* Render NASCAR Sub-Menu Content */}
          <div>
            <ErrorBoundary showDetails={true}>
              <Suspense fallback={<TabLoading />}>
                {selectedNascarTab === "Fantasy" && <FantasyMainScreen />}
                {selectedNascarTab === "Statistics" && (
                  <StatisticsMainScreen themeMode={themeMode} />
                )}
                {selectedNascarTab === "Favourite_driver" && (
                  <FavoriteDriverMenu themeMode={themeMode} />
                )}
                {selectedNascarTab === "History" && <HistoryMainScreen />}
              </Suspense>
            </ErrorBoundary>
          </div>
        </Box>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
